import { parseSearchFiltersToUrl } from '~/apps/pu-links/infrastructure/searchUrlHelpers'
import { getTransformObject } from '~/common/photos/transforms'
import type { Logger } from '~/composables/useGtmLogRequest'
import { injectable } from '~/container'
import { getLangFallbackUrl } from '~/lang/utils/langfetch'
import { getWKTPointFromGeoPoint } from '~/utility/geo/GeoLocation'
import { getPointFromStringOrDefault } from '~/utility/geo/latLng'
import { keysToCamel } from '~/utility/toCamel'
import {
  CampsiteMapsSearchResult,
  MapSearchCampsitesResults,
  SearchFilters,
} from '../schemas'
import { formatBounds } from './bounds'
import { fetchWithCache } from '../../../utility/fetchWithCache'

// const SEARCH_RESULTS_LIMIT = 200

@injectable()
export class FetchCampsitePriceMapSearchService {
  async search(
    filters: SearchFilters,
    langCode: string,
    logger: Logger,
    fetchOptions?: any,
  ): Promise<MapSearchCampsitesResults> {
    const params = this.filtersToParams(filters).toString()
    const fieldsToShow = [
      'campsite_id',
      'categories',
      'category_ids',
      'hierarchy_text_short',
      'id',
      'name',
      'path',
      'cheapest_price',
      'point',
      'primary_photo',
      'rate_count',
      'rating',
      'rating100',
      'slug',
      'url',
    ]
    const url = getLangFallbackUrl(
      langCode,
      `/search2/price/?fields=${fieldsToShow.join(
        ',',
      )}&${params}&sort=magic&facet_counts=none&seo=0`,
    )
    const response: any = await fetchWithCache(url, fetchOptions)
    return {
      bounds: response.bounding_box
        ? formatBounds(response.bounding_box)
        : null,
      campsites: this.getFilteredResults(
        this.formatResults(keysToCamel(response.results)),
      ),
      pagination: {
        currentPage: Number(response.page),
        totalPages: response.num_pages,
        totalCount: response.count,
      },
      title: response.title,
      heading: response.heading,
      bookable: true,
      category: response.category,
      count: response.count,
      path: response.path,
      point: response.point,
      leadPrice: response.lead_price,
      sortOptions: response.sort_options,
      sortOption: response.sort || response.sort_field,
      maxPrice: response.result_max_price,
      within: keysToCamel(response.within),
    }
  }

  private getFilteredResults(
    results: CampsiteMapsSearchResult[],
  ): CampsiteMapsSearchResult[] {
    return results.filter((r) => r.name !== 'noindex')
  }

  private formatResults(results: any[]): CampsiteMapsSearchResult[] {
    return results
      .filter((campsite) => !!campsite.point)
      .map((campsite) => {
        const point = getWKTPointFromGeoPoint(
          getPointFromStringOrDefault(campsite.point),
        )
        const { lat, lng } = getPointFromStringOrDefault(point)
        return {
          ...campsite,
          id: campsite.campsiteId.toString(),
          status: 'bookable',
          bookable: true,
          price: campsite.cheapestPrice,
          hierarchy: campsite.hierarchyTextShort,
          rating: Number(campsite.rating),
          rating10: Number(campsite.rating) * 2,
          point,
          lat,
          lng,
          photo: campsite.primaryPhoto
            ? {
                url: getTransformObject(
                  'map',
                  campsite.primaryPhoto.url.masterImage,
                ),
                caption: campsite.primaryPhoto?.caption || '',
              }
            : null,
        }
      })
  }

  private filtersToParams(filters: SearchFilters) {
    if (!filters.dates) {
      throw new Error('no dates')
    }
    const params = new URLSearchParams()
    for (const param of parseSearchFiltersToUrl({
      ...filters,
      // limit: SEARCH_RESULTS_LIMIT,
    })) {
      params.append(param.filterName, param.filterValue)
    }
    if (params.get('bounds')) {
      // remove bounds if path
      if (params.get('path')) {
        params.delete('bounds')
      }
    }
    if (filters.engine) {
      params.set('engine', filters.engine.toString())
    }
    return params
  }
}
